import { f7, useStore } from 'framework7-react';
import { FC, useEffect, useState } from 'react';
import {
  getSubscriptionPlansApi,
  getSubscriptionMakersApi,
  getSubscriptionCarnamesApi,
  postSubscriptionApplyApi,
} from '@/api/subscription';
import { getValidWarranties } from '@/components/pages/WarrantyManagement/WarrantyManagementHelper';
import { PageTemplate } from '@/components/uiParts/Template/PageTemplate/PageTemplate';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { Car, Warranty } from '@/types/api/customerApi';
import { SubscriptionPlan } from '@/types/api/subscription';

type CarSelectMode = 'registered' | 'manual';

interface Maker {
  mk_cd: number;
  mk_nm: string;
}
interface CarName {
  carnm_cd: number;
  car_nm: string;
}

export const SubscriptionConfirmPage: FC = () => {
  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const authInfo = store.state.authInfo as any;
  const ownedCars = useStore(store, 'carsInfo') as Car[];
  const { warranties } = store.state as { warranties: Warranty[] };
  const validWarranties = getValidWarranties(warranties);
  const warrantyCarIds = new Set(
    validWarranties.map((w: any) => w.t_stock_car_id).filter(Boolean),
  );
  const allCars: Car[] = (ownedCars || []).filter(
    (car) => !warrantyCarIds.has(car.t_stock_car_id),
  );

  // 車両選択モード
  const [carSelectMode, setCarSelectMode] = useState<CarSelectMode>(
    allCars.length > 0 ? 'registered' : 'manual',
  );
  const [selectedCar, setSelectedCar] = useState<Car | null>(null);

  // メーカー/車種選択
  const [makers, setMakers] = useState<Maker[]>([]);
  const [carnames, setCarnames] = useState<CarName[]>([]);
  const [selectedMkCd, setSelectedMkCd] = useState<number | null>(null);
  const [selectedCarnmCd, setSelectedCarnmCd] = useState<number | null>(null);
  const [selectedMakerName, setSelectedMakerName] = useState('');
  const [selectedCarName, setSelectedCarName] = useState('');

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const res = await getSubscriptionPlansApi();
        if (res.data.success && res.data.data?.plans?.[0]) {
          setPlan(res.data.data.plans[0]);
        }
      } catch (e) {
        console.error('プラン取得エラー', e);
      }
    };
    fetchPlan();
  }, []);

  // メーカー一覧取得
  useEffect(() => {
    if (carSelectMode !== 'manual') return;
    const fetchMakers = async () => {
      try {
        const res = await getSubscriptionMakersApi();
        if (res.data.success) {
          setMakers(res.data.data || []);
        }
      } catch (e) {
        console.error('メーカー取得エラー', e);
      }
    };
    if (makers.length === 0) fetchMakers();
  }, [carSelectMode]);

  // 車種一覧取得（メーカー選択時）
  useEffect(() => {
    if (!selectedMkCd) {
      setCarnames([]);
      return;
    }
    const fetchCarnames = async () => {
      try {
        const res = await getSubscriptionCarnamesApi(selectedMkCd);
        if (res.data.success) {
          setCarnames(res.data.data || []);
        }
      } catch (e) {
        console.error('車種取得エラー', e);
      }
    };
    fetchCarnames();
  }, [selectedMkCd]);

  const handleSubmit = async () => {
    if (isSubmitting || !plan) return;

    // 車両情報の確認
    const hasRegisteredCar = carSelectMode === 'registered' && selectedCar;
    const hasManualCar =
      carSelectMode === 'manual' && selectedMkCd && selectedCarnmCd;
    if (!hasRegisteredCar && !hasManualCar) return;

    setIsSubmitting(true);
    try {
      const params: any = {
        m_customer_id: authInfo.m_customer_id,
        m_subscription_plan_id: plan.id,
      };
      if (hasRegisteredCar) {
        params.t_stock_car_id = selectedCar!.t_stock_car_id;
      } else {
        params.mk_cd = selectedMkCd;
        params.carnm_cd = selectedCarnmCd;
      }

      const res = await postSubscriptionApplyApi(params);

      if (res.data.success) {
        f7.view.main.router.navigate(paths.subscriptionComplete, {
          props: {
            subscriptionId: res.data.data?.t_subscription_id,
            planName: plan.plan_name,
            monthlyFee: plan.monthly_fee,
            hasRegisteredCar: false,
            tStockCarId: hasRegisteredCar
              ? selectedCar!.t_stock_car_id
              : undefined,
          },
        });
      } else {
        f7.dialog.alert(res.data.error_message || '申込に失敗しました');
      }
    } catch (e: any) {
      f7.dialog.alert(e.message || '申込処理中にエラーが発生しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClickTerm = (termType: number) => {
    f7.view.main.router.navigate(paths.terms, {
      props: { termType },
    });
  };

  const isCarSelected =
    (carSelectMode === 'registered' && selectedCar) ||
    (carSelectMode === 'manual' && selectedMkCd && selectedCarnmCd);

  return (
    <PageTemplate
      pageName="subscription-confirm"
      title="ご購入内容の確認"
      showNavBack
    >
      <div style={{ padding: '16px' }}>
        {/* プラン */}
        <div
          style={{
            padding: '20px',
            marginBottom: '16px',
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: '1px solid #e0e0e0',
          }}
        >
          <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
            プラン
          </div>
          <div
            style={{
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '4px',
            }}
          >
            {plan?.plan_name || '読み込み中...'}
          </div>
          {plan && (
            <div style={{ fontSize: '22px', fontWeight: 'bold' }}>
              {plan.monthly_fee.toLocaleString()}
              <span style={{ fontSize: '14px', fontWeight: 'normal' }}>
                円/月（税込）
              </span>
            </div>
          )}
        </div>

        {/* 車両選択 */}
        <div
          style={{
            padding: '20px',
            marginBottom: '16px',
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: '1px solid #e0e0e0',
          }}
        >
          <div
            style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}
          >
            対象の車両
          </div>

          {/* 切り替えラジオ */}
          {allCars.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <label
                onClick={() => {
                  setCarSelectMode('registered');
                  setSelectedMkCd(null);
                  setSelectedCarnmCd(null);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                <span
                  style={{
                    width: '23px',
                    height: '23px',
                    borderRadius: '50%',
                    flexShrink: 0,
                    border: '2px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {carSelectMode === 'registered' && (
                    <span
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#1E55E6',
                      }}
                    />
                  )}
                </span>
                既に登録されている車から選ぶ
              </label>
              <label
                onClick={() => {
                  setCarSelectMode('manual');
                  setSelectedCar(null);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                <span
                  style={{
                    width: '23px',
                    height: '23px',
                    borderRadius: '50%',
                    flexShrink: 0,
                    border: '2px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {carSelectMode === 'manual' && (
                    <span
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#1E55E6',
                      }}
                    />
                  )}
                </span>
                新しく車を登録する
              </label>
            </div>
          )}

          {/* 登録済み車両リスト */}
          {carSelectMode === 'registered' &&
            allCars.map((car) => {
              const isSelected =
                selectedCar?.t_stock_car_id === car.t_stock_car_id;
              return (
                <div
                  key={car.m_car_id || car.t_stock_car_id}
                  onClick={() => setSelectedCar(isSelected ? null : car)}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    borderRadius: '8px',
                    border: isSelected
                      ? '2px solid #1E55E6'
                      : '1px solid #c9c9c9',
                    backgroundColor: isSelected
                      ? 'rgba(30,85,230,0.1)'
                      : '#fff',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontSize: '15px', fontWeight: 'bold' }}>
                    {car.maker_nm} {car.car_nm}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#666',
                      marginTop: '2px',
                    }}
                  >
                    {car.class_no} {car.hiragana} {car.registry_no}
                  </div>
                </div>
              );
            })}

          {/* メーカー/車種セレクト */}
          {carSelectMode === 'manual' && (
            <div>
              <div style={{ marginBottom: '12px' }}>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    marginBottom: '4px',
                  }}
                >
                  メーカー{' '}
                  <span style={{ color: '#E65100', fontSize: '12px' }}>
                    必須
                  </span>
                </div>
                <select
                  value={selectedMkCd || ''}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setSelectedMkCd(val || null);
                    setSelectedCarnmCd(null);
                    setSelectedCarName('');
                    const maker = makers.find((m) => m.mk_cd === val);
                    setSelectedMakerName(maker?.mk_nm || '');
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '15px',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                    backgroundColor: '#fff',
                    appearance: 'auto',
                  }}
                >
                  <option value="">選択してください</option>
                  {makers.map((m) => (
                    <option key={m.mk_cd} value={m.mk_cd}>
                      {m.mk_nm}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '8px' }}>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    marginBottom: '4px',
                  }}
                >
                  車種{' '}
                  <span style={{ color: '#E65100', fontSize: '12px' }}>
                    必須
                  </span>
                </div>
                <select
                  value={selectedCarnmCd || ''}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setSelectedCarnmCd(val || null);
                    const cn = carnames.find((c) => c.carnm_cd === val);
                    setSelectedCarName(cn?.car_nm || '');
                  }}
                  disabled={!selectedMkCd}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '15px',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                    backgroundColor: selectedMkCd ? '#fff' : '#f5f5f5',
                    appearance: 'auto',
                  }}
                >
                  <option value="">選択してください</option>
                  {carnames.map((c) => (
                    <option key={c.carnm_cd} value={c.carnm_cd}>
                      {c.car_nm}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* 規約同意 */}
        <div style={{ marginBottom: '24px' }}>
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '14px',
              padding: '20px 16px',
              marginBottom: '16px',
            }}
          >
            <div
              style={{
                fontSize: '14px',
                fontWeight: 'bold',
                marginBottom: '12px',
              }}
            >
              サービス利用規約
            </div>
            <div
              style={{
                fontSize: '13px',
                color: '#666',
                lineHeight: 1.6,
                marginBottom: '16px',
              }}
            >
              お申し込みの前に、以下の利用規約を必ずご確認ください。
            </div>
            {plan?.term_type && (
              <div
                onClick={() => handleClickTerm(plan.term_type!)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 0',
                  borderTop: '1px solid #f0f0f0',
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: '14px', color: '#1E55E6' }}>
                  サブスクリプションサービス利用規約
                </span>
                <i
                  className="icon-right"
                  style={{ fontSize: '12px', color: '#bbb' }}
                />
              </div>
            )}
            {plan?.coverages
              ?.filter((cov) => cov.term_type)
              .map((cov) => (
                <div
                  key={cov.id}
                  onClick={() => handleClickTerm(cov.term_type!)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 0',
                    borderTop: '1px solid #f0f0f0',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: '14px', color: '#1E55E6' }}>
                    {cov.coverage_name}利用規約
                  </span>
                  <i
                    className="icon-right"
                    style={{ fontSize: '12px', color: '#bbb' }}
                  />
                </div>
              ))}
          </div>
          <div
            onClick={() => setTermsAgreed(!termsAgreed)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            <div
              style={{
                width: '22px',
                height: '22px',
                flexShrink: 0,
                borderRadius: '4px',
                border: termsAgreed ? 'none' : '2px solid #ccc',
                backgroundColor: termsAgreed ? '#1E55E6' : '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              {termsAgreed ? '✓' : ''}
            </div>
            <span>利用規約に同意する</span>
          </div>
        </div>

        {/* 申込ボタン */}
        <button
          onClick={handleSubmit}
          disabled={!isCarSelected || !termsAgreed || isSubmitting || !plan}
          style={{
            width: '100%',
            padding: '16px',
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#fff',
            backgroundColor:
              isCarSelected && termsAgreed && !isSubmitting
                ? '#1E55E6'
                : '#ccc',
            border: 'none',
            borderRadius: '28px',
            cursor:
              isCarSelected && termsAgreed && !isSubmitting
                ? 'pointer'
                : 'not-allowed',
          }}
        >
          {isSubmitting ? '処理中...' : '申し込む'}
        </button>
      </div>
    </PageTemplate>
  );
};
